// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Holding something still.
 *
 * The property line, the limit of disturbance, the building footprint — a
 * logistics plan has a layer of things that are surveyed facts, and a layer of
 * things that move every week. Once the first layer is right, every drag aimed
 * at the second is a chance to nudge it a foot and not notice; the drawing is
 * then wrong in the one way nobody re-checks.
 *
 * Locking is what every markup tool offers for this. A locked element still
 * selects, so you can read it, copy it and find the way to unlock it — it just
 * refuses to be moved, reshaped, turned, nudged or deleted until you say so.
 * Refusing to select it as well would be stricter and worse: the only route
 * back would be a menu somewhere else.
 */

import type { SiteLogistics } from "./logistics";
import { findElement, LIST, type ElementKind } from "./logisticsClipboard";

/** Anything on the plan that can be held still. */
export interface Lockable { id: string; locked?: boolean }

export function isLocked(el: Lockable | null | undefined): boolean {
  return !!el?.locked;
}

/** Whether the element with this id is locked. */
export function lockedById(sl: SiteLogistics | null | undefined, id: string | null): boolean {
  if (!sl || !id) return false;
  return isLocked(findElement(sl, id)?.el);
}

/** The plan with that element locked or unlocked. */
export function setLock(sl: SiteLogistics, id: string | null, locked: boolean): SiteLogistics {
  const found = findElement(sl, id);
  if (!found) return sl;
  const key = LIST[found.kind];
  const list = ((sl as any)[key] as Lockable[]) || [];
  return {
    ...sl,
    // Unlocking drops the flag rather than storing false, so a plan nobody has
    // locked anything on saves exactly as it did before this existed.
    [key]: list.map((e) => (e.id !== id ? e
      : locked ? { ...e, locked: true } : (({ locked: _drop, ...rest }) => rest)(e as any))),
  } as SiteLogistics;
}

export function toggleLock(sl: SiteLogistics, id: string | null): SiteLogistics {
  return setLock(sl, id, !lockedById(sl, id));
}

/** How many elements are locked, across every kind. */
export function countLocked(sl: SiteLogistics | null | undefined): number {
  if (!sl) return 0;
  let n = 0;
  for (const key of Object.values(LIST)) {
    for (const e of (((sl as any)[key] as Lockable[]) || [])) if (isLocked(e)) n++;
  }
  return n;
}

/** Everything unlocked again, for when a plan has been pinned down too far. */
export function unlockAll(sl: SiteLogistics): SiteLogistics {
  const next: any = { ...sl };
  for (const key of Object.values(LIST)) {
    const list = ((sl as any)[key] as Lockable[]) || [];
    next[key] = list.map((e) => (isLocked(e) ? (({ locked: _drop, ...rest }) => rest)(e as any) : e));
  }
  return next as SiteLogistics;
}

/**
 * The plan cleared of everything that was not locked.
 *
 * "Clear all" is the one command where locking has to be allowed to contradict
 * the word on the button, because keeping the survey through a wipe of the
 * week's logistics is the whole reason to lock it. How many were kept is
 * returned so the user can be told rather than left to notice.
 */
export function clearUnlocked(sl: SiteLogistics): { sl: SiteLogistics; kept: number } {
  const next: any = { ...sl };
  let kept = 0;
  for (const kind of Object.keys(LIST) as ElementKind[]) {
    // Free text is cleared with everything else: "clear all" has never touched
    // it, and this is not the place to change that.
    if (kind === "text") continue;
    const key = LIST[kind];
    const list = ((sl as any)[key] as Lockable[]) || [];
    const keep = list.filter(isLocked);
    kept += keep.length;
    next[key] = keep;
  }
  return { sl: next as SiteLogistics, kept };
}
